import { useDispatch } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import {
  TSetupEditContractCategoryErrorType,
  TSetupEditContractCategoryFormType
} from '../General/EditContractCategory/types'
import { TOption } from '../../../components/Select/types'
import { useContractCategoryDetailsContext } from '../Providers/ContractCategoryDetailsContextProvider'
import { Form } from 'antd'
import { setLoading } from '../../../redux/store/common/slice'
import {
  editCCDetailsRequestAsync,
  getOrganizationOptions,
  getContractCategoriesOptions,
  getLedByOptions,
  setupCCDetailsRequestAsync
} from '../api'
import { AxiosResponse } from 'axios'
import useRouter from '../../../hooks/useRouter'
import { useNavigate } from 'react-router'
import { routes } from '../../../router'
import { LED_BY_VALUE } from '../../../components/LeadByIndicator/constants'

export const useSetupEditContractCategory = (isNew: boolean) => {
  const dispatch = useDispatch()
  const [isActive, setIsActive] = useState(false)
  const [isLedByLoading, _setIsLedByLoading] = useState(false)
  const [errors, setErrors] = useState<TSetupEditContractCategoryErrorType>({})
  const [predefinedCCValue, setPredefinedCCValue] = useState('')
  const [selectedContractCategory, setSelectedContractCategory] =
    useState<string>()
  const [contractCategories, setContractCategories] = useState<TOption[]>([])
  const [ledByOptions, setLedByOptions] = useState<TOption[]>([])

  const [organizationOptions, setOrganizationOptions] = useState<TOption[]>([])

  const [form] = Form.useForm<TSetupEditContractCategoryFormType>()

  const {
    query: { contract_category }
  } = useRouter()
  const navigate = useNavigate()
  const router = useRouter()

  const {
    state: {
      details: {
        compliance_requirement,
        uuid,
        organization,
        not_measuring_compliance
      }
    },
    actions
  } = useContractCategoryDetailsContext()

  const notMeasuringCompliance = Form.useWatch('not_measuring_compliance', form)
  const complianceRequirement = Form.useWatch('compliance_requirement', form)

  useEffect(() => {
    if (notMeasuringCompliance && complianceRequirement) {
      form.setFieldsValue({
        compliance_requirement: '0'
      })
    } else if (!notMeasuringCompliance && complianceRequirement === '0') {
      form.setFieldsValue({
        compliance_requirement: undefined
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notMeasuringCompliance, complianceRequirement])

  useEffect(() => {
    if (contract_category && !!contractCategories.length) {
      if (contractCategories.find((cc) => cc.value === contract_category)) {
        form.setFieldsValue({
          contract_category
        })
        setPredefinedCCValue(contract_category)
        setSelectedContractCategory(contract_category)
      }
      navigate(location.pathname, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract_category, contractCategories])

  useEffect(() => {
    if (isNew) {
      dispatch(setLoading(true))
      getContractCategoriesOptions({})
        .then((results) => {
          setContractCategories(
            results?.data.results.map((r) => {
              return {
                label: r.name,
                value: r.uuid
              }
            })
          )
        })
        .finally(() => dispatch(setLoading(false)))
    }
  }, [dispatch, isNew])

  const selectedLedBy = Form.useWatch('led_by', form)

  useEffect(() => {
    if (
      (selectedLedBy === LED_BY_VALUE.LIFELINE_FOR_COMMUNITY ||
        selectedLedBy === LED_BY_VALUE.LIFELINE_FOR_HEALTH_SYSTEM) &&
      selectedContractCategory
    ) {
      form.setFieldsValue({
        organization_id: undefined
      })
      getOrganizationOptions({
        led_by: selectedLedBy,
        contract_category_id: selectedContractCategory
      }).then((results) => {
        setOrganizationOptions(
          results?.data.results.map((r) => {
            return {
              label: r.name,
              value: r.uuid
            }
          })
        )
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLedBy, selectedContractCategory])

  useEffect(() => {
    if (selectedContractCategory) {
      _setIsLedByLoading(true)
      getLedByOptions({ category: selectedContractCategory })
        .then((results) => {
          setLedByOptions(
            results?.data.results.map((r) => {
              return {
                label: r.label,
                value: r.value
              }
            })
          )
        })
        .finally(() => _setIsLedByLoading(false))
    }
  }, [selectedContractCategory])

  const resetFormValues = useCallback(() => {
    if (!isNew) {
      form.setFieldsValue({
        compliance_requirement: compliance_requirement ?? '',
        not_measuring_compliance,
        organization_id: organization ?? ''
      })
    } else {
      const fieldsToReset = [
        'compliance_requirement',
        'led_by',
        'not_measuring_compliance'
      ]

      if (predefinedCCValue) {
        form.setFieldsValue({
          contract_category: predefinedCCValue
        })
      } else {
        fieldsToReset.push('contract_category')
      }
      form.resetFields(fieldsToReset)
    }
    setErrors({})
  }, [
    isNew,
    form,
    compliance_requirement,
    not_measuring_compliance,
    organization,
    predefinedCCValue
  ])

  useEffect(() => {
    resetFormValues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compliance_requirement, isNew])

  const onSubmit = useCallback(
    async (values: TSetupEditContractCategoryFormType) => {
      dispatch(setLoading(true))
      let newValues = {
        not_measuring_compliance: values.not_measuring_compliance
      } as TSetupEditContractCategoryFormType
      if (values.compliance_requirement)
        newValues.compliance_requirement = values.compliance_requirement
      if (values.not_measuring_compliance)
        newValues.compliance_requirement = '0'
      if (isNew) {
        newValues = {
          ...newValues,
          led_by: values.led_by,
          contract_category: values.contract_category,
          organization_id: values.organization_id
        }
      }
      try {
        setIsActive(false)
        if (isNew) {
          const result = await setupCCDetailsRequestAsync(newValues)
          if (result.data?.uuid) {
            router.push(
              `${routes.contracts}${routes.contractCategories}/${result.data?.uuid}`,
              { replace: true }
            )
          }
        } else {
          await editCCDetailsRequestAsync(uuid, newValues)
          actions.getCCDetailsAsync()
          setErrors({})
        }
      } catch (error) {
        const typedResponse = error as AxiosResponse
        setErrors(typedResponse.data)
        setIsActive(true)
      } finally {
        dispatch(setLoading(false))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, editCCDetailsRequestAsync, form, isNew, isActive, setIsActive]
  )

  return {
    onSubmit,
    form,
    errors,
    isActive,
    setIsActive,
    resetFormValues,
    ledByOptions,
    isLedByLoading,
    contractCategories,
    selectedContractCategory,
    setSelectedContractCategory,
    notMeasuringCompliance,
    selectedLedBy,
    organizationOptions
  }
}
